import { DateTime } from 'luxon';

type TimeRange = {
  started_at?: string | null;
  ended_at?: string | null;
};

/**
 * An item is owned by an entry iff the entry has a non-null range AND the
 * item's range is fully contained within it (boundary-inclusive). Epoch-ms
 * compare so mixed ISO formats (UTC `Z` vs offset `+02:00`) for the same
 * instant resolve correctly. Mirrors the server-side SQL ownership predicate.
 *
 * The FK column `timesheet_proposal_entry_id` on items is provenance only;
 * client code groups by timestamp via this predicate.
 */
export const isItemContainedByEntry = (item: TimeRange, entry: TimeRange): boolean => {
  if (!entry.started_at || !entry.ended_at) return false;
  if (!item.started_at || !item.ended_at) return false;
  const entryStartMs = DateTime.fromISO(entry.started_at).toMillis();
  const entryEndMs = DateTime.fromISO(entry.ended_at).toMillis();
  const itemStartMs = DateTime.fromISO(item.started_at).toMillis();
  const itemEndMs = DateTime.fromISO(item.ended_at).toMillis();
  return entryStartMs <= itemStartMs && itemEndMs <= entryEndMs;
};

/**
 * Single sweep that buckets items by the entry containing them. Items contained
 * by no entry land in `unassigned`. Snap-to-boundary invariant: an item should
 * be in at most one entry; if two claim it the first match wins and we warn.
 */
export const partitionItemsByContainment = <
  I extends TimeRange & { id: string | number },
  E extends TimeRange & { id: string | number },
>(
  items: I[],
  entries: E[]
): { byEntryId: Map<E['id'], I[]>; unassigned: I[] } => {
  const byEntryId = new Map<E['id'], I[]>();
  const unassigned: I[] = [];

  for (const item of items) {
    let owner: E | undefined;
    for (const entry of entries) {
      if (isItemContainedByEntry(item, entry)) {
        if (owner) {
          // eslint-disable-next-line no-console
          console.warn(
            `[TT-4761] item ${item.id} contained by multiple entries: ${owner.id}, ${entry.id}`
          );
          break;
        }
        owner = entry;
      }
    }
    if (owner) {
      const bucket = byEntryId.get(owner.id);
      if (bucket) bucket.push(item);
      else byEntryId.set(owner.id, [item]);
    } else {
      unassigned.push(item);
    }
  }

  return { byEntryId, unassigned };
};

/**
 * Of the given entry IDs, those containing at least one item. Drag-claiming
 * empty timeline space creates an entry with no activities; regenerating its
 * note hits the engine's no-activities guard and fails with user-visible error
 * toasts, so callers gate note regeneration on this.
 */
export const entryIdsWithItems = <
  I extends TimeRange & { id: string | number },
  E extends TimeRange & { id: string | number },
>(
  entryIds: Iterable<E['id']>,
  items: I[],
  entries: E[]
): E['id'][] => {
  const { byEntryId } = partitionItemsByContainment(items, entries);
  return Array.from(entryIds).filter(id => (byEntryId.get(id)?.length ?? 0) > 0);
};
